// -------------------------------------
// FINAL FULL VERSION — INCLUDING PHOTO, DL, INVOICE, MODAL
// -------------------------------------
import React, { useState } from "react"
import PropTypes from "prop-types"
import { Card, CardBody, Col, Row, Button } from "reactstrap"
import { createPortal } from "react-dom"
import downloadVerificationLetterPDF from "./downloadVerificationLetterPdf"

const Overview = ({ license }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [fullName, setFullName] = useState("")
  const [licenseNumber, setLicenseNumber] = useState("")
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  // -----------------------------
  // OPEN MODAL
  // -----------------------------
  const openVerificationModal = () => {
    const first = license?.customerProfile?.firstName || ""
    const last = license?.customerProfile?.lastName || ""
    const dl = license?.customerProfile?.drivingLicenseNumber || ""

    setFullName(`${first} ${last}`.trim())
    setLicenseNumber(dl)
    setIsConfirmed(false)
    setIsModalOpen(true)
  }

  // -----------------------------
  // CLOSE MODAL
  // -----------------------------
  const closeVerificationModal = () => {
    if (!isDownloading) setIsModalOpen(false)
  }

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const nameValid = fullName.trim().length > 0
  const licenseValid = licenseNumber.trim().length > 0
  const formValid = nameValid && licenseValid && isConfirmed

  // -----------------------------
  // DOWNLOAD PDF
  // -----------------------------
  const handleDownload = async () => {
    if (!formValid) return

    try {
      setIsDownloading(true)

      await downloadVerificationLetterPDF({
        grn: license?.grn,
        fullName: fullName.trim(),
        licenseNumber: licenseNumber.trim(),
        headerImageUrl: "https://res.cloudinary.com/developerqa/image/upload/v1763286318/amadeus-local-solutions/idl/header.png",
        signatureImageUrl: "https://res.cloudinary.com/developerqa/image/upload/v1763286319/amadeus-local-solutions/idl/signature.png",
        emailIconUrl:"https://res.cloudinary.com/developerqa/image/upload/v1763301060/amadeus-local-solutions/idl/email_icon.png",
        phoneIconUrl:"https://res.cloudinary.com/developerqa/image/upload/v1763301064/amadeus-local-solutions/idl/phone_icon.png",
        locationIconUrl:"https://res.cloudinary.com/developerqa/image/upload/v1763301437/amadeus-local-solutions/idl/location_icon.png",
      })

      setIsModalOpen(false)
    } catch (err) {
      console.error("PDF generation failed:", err)
      alert("Unable to generate the verification letter. Please try again.")
    } finally {
      setIsDownloading(false)
    }
  }

  // -----------------------------
  // MODAL RENDER (PORTAL)
  // -----------------------------
  const modal = isModalOpen
    ? createPortal(
        <>
          {/* BACKDROP */}
          <div
            className="modal-backdrop fade show"
            style={{
              zIndex: 2000,
              position: "fixed",
              inset: 0
            }}
            onClick={closeVerificationModal}
          ></div>

          {/* MODAL */}
          <div
  className="modal fade show"
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "fixed",
    inset: 0,
    zIndex: 2001,
    padding: "20px",
    background: "rgba(33, 18, 18, 0.05)",
  }}
  role="dialog"
  aria-modal="true"
>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">Verification Letter Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeVerificationModal}
                  />
                </div>

                <div className="modal-body">

                  {/* FULL NAME */}
                  <div className="mb-3">
                    <label htmlFor="verificationFullName" className="form-label">
                      Name (First & Last)
                    </label>
                    <input
                      id="verificationFullName"
                      className="form-control"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                    {!nameValid && (
                      <small className="text-danger">Name is required.</small>
                    )}
                  </div>

                  {/* LICENSE NUMBER */}
                  <div className="mb-3">
                    <label htmlFor="verificationLicenseNumber" className="form-label">
                      License Number
                    </label>
                    <input
                      id="verificationLicenseNumber"
                      className="form-control"
                      value={licenseNumber}
                      onChange={(e) => setLicenseNumber(e.target.value)}
                    />
                    {!licenseValid && (
                      <small className="text-danger">License number is required.</small>
                    )}
                  </div>

                  {/* CUSTOM TOGGLE (REPLACES CHECKBOX) */}
                  <div 
                    className="d-flex align-items-center mt-3"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => setIsConfirmed(!isConfirmed)}
                  >
                    {/* Toggle Track */}
                    <div 
                      style={{
                        width: "48px",
                        height: "24px",
                        borderRadius: "24px",
                        backgroundColor: isConfirmed ? "#4caf50" : "#ccc",
                        position: "relative",
                        transition: "all 0.2s ease"
                      }}
                    >
                      {/* Toggle Knob */}
                      <div 
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                          position: "absolute",
                          top: "2px",
                          left: isConfirmed ? "26px" : "2px",
                          transition: "all 0.2s ease",
                          boxShadow: "0 0 4px rgba(0,0,0,0.3)"
                        }}
                      ></div>
                    </div>

                    {/* Label */}
                    <span className="ms-3">
                      I confirm that the name and license number are correct.
                    </span>
                  </div>

                </div>

                <div className="modal-footer">
                  <Button
                    color="secondary"
                    onClick={closeVerificationModal}
                    disabled={isDownloading}
                  >
                    Cancel
                  </Button>

                  <Button
                    color="primary"
                    onClick={handleDownload}
                    disabled={!formValid || isDownloading}
                  >
                    {isDownloading ? "Generating..." : "Download Verification Letter (PDF)"}
                  </Button>
                </div>

              </div>
            </div>
          </div>
        </>,
        document.body
      )
    : null

  // -----------------------------------
  // PAGE UI
  // -----------------------------------
  return (
    <>
      {/* GRN */}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <h5 className="fw-semibold">{license?.grn}</h5>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* CUSTOMER PROFILE */}
      <Row>
        <Col>
          <Card>
            <CardBody>
              <h5 className="fw-semibold">Customer Profile</h5>

              <div className="table-responsive">
                <table className="table">
                  <tbody>
                    <tr><th>First Name</th><td>{license?.customerProfile?.firstName}</td></tr>
                    <tr><th>Last Name</th><td>{license?.customerProfile?.lastName}</td></tr>
                    <tr><th>BirthDate</th><td>{license?.customerProfile?.birthDate}</td></tr>
                    <tr><th>Email</th><td>{license?.customerProfile?.emailAddress}</td></tr>
                    <tr><th>Mobile Number</th><td>{license?.mobileNumber}</td></tr>
                    <tr><th>License Number</th><td>{license?.customerProfile?.drivingLicenseNumber}</td></tr>
                    <tr><th>License Class</th><td>{license?.customerProfile?.drivingLicenseClass}</td></tr>
                    <tr><th>Nationality</th><td>{license?.customerProfile?.drivingLicenseNationality}</td></tr>
                    <tr><th>Place of Birth</th><td>{license?.customerProfile?.drivingLicensePlaceOfBirth}</td></tr>
                    <tr><th>Address</th><td>{license?.customerProfile?.drivingLicenseAddress}</td></tr>
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* PHOTO - DL - INVOICE */}
      <Row>
        <Col>
          <Card>
            <CardBody className="text-center">
              <a href={license?.customerProfile?.customerPhotoUrl} target="_blank" rel="noreferrer">
                <i className="bx bx-user-circle bx-lg" /><br />
                <span className="fw-semibold">Photo</span>
              </a>
            </CardBody>
          </Card>
        </Col>

        <Col>
          <Card>
            <CardBody className="text-center">
              <a href={license?.customerProfile?.nationalDrivingLicenseUrl} target="_blank" rel="noreferrer">
                <i className="bx bx-id-card bx-lg" /><br />
                <span className="fw-semibold">Driving License</span>
              </a>
            </CardBody>
          </Card>
        </Col>

        <Col>
          <Card>
            <CardBody className="text-center">
              <a href={license?.invoiceUrl} target="_blank" rel="noreferrer">
                <i className="bx bx-file bx-lg" /><br />
                <span className="fw-semibold">Invoice</span>
              </a>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* VERIFICATION LETTER BUTTON */}
      <Row>
        <Col>
          <Card>
            <CardBody className="text-center">
              <button
                className="btn btn-link fw-semibold p-0"
                onClick={openVerificationModal}
              >
                <i className="bx bx-user-circle bx-lg" /><br />
                Verification Letter
              </button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {modal}
    </>
  )
}

Overview.propTypes = {
  license: PropTypes.shape({
    grn: PropTypes.string,
    mobileNumber: PropTypes.string,
    invoiceUrl: PropTypes.string,
    customerProfile: PropTypes.object
  })
}

export default Overview